import type { Vehicle } from './types'

/**
 * Touring vehicle presets — MPG and tank capacity based on industry averages
 * for loaded touring vehicles. Tank-capacity sources:
 *   - Sedan / SUV: typical mid-size sedan tank (Toyota Camry-class)
 *   - 15-pass van: Ford Transit / Chevy Express specs
 *   - Sprinter: Mercedes Sprinter 2500/3500 standard tank
 *   - 26' box truck: Hino/Isuzu medium-duty tank typical
 *   - Prevost nightliner: dual saddle tank Prevost H3-45 (~200 gal usable)
 *   - Semi / gear truck: dual saddle tanks, Class 8 (~200-300 gal total)
 *
 * "Usable" capacity — touring crews don't drive to a 5-gallon needle.
 * Users can override per-vehicle via the tank-capacity input.
 *
 * `clearance` is each class's loaded envelope (height rounded UP, GVWR as
 * weight) for bridge/weight-aware routing. Conservative on purpose: a route
 * that avoids a bridge the bus would have cleared costs a few miles; the
 * opposite costs a roof.
 *
 * `auxPower` is the generator/reefer preset burn rate (gal/hr) at a typical
 * load: a 20–25 kW bus genset running A/C ≈ 0.75, a trailer reefer ≈ 0.8.
 * Users toggle it per route and can override the rate.
 */
export const VEHICLES: Vehicle[] = [
	{
		id: 'sedan',
		name: 'Sedan / SUV',
		description: 'Advance scout, production manager',
		baseMpg: 28,
		defaultFuel: 'gas',
		weightClass: 'light',
		icon: 'Car',
		tankCapacityGallons: 14,
	},
	{
		id: 'van-15',
		name: '15-Passenger Van',
		description: 'Band/crew transport',
		baseMpg: 13,
		defaultFuel: 'gas',
		weightClass: 'heavy',
		icon: 'Bus',
		tankCapacityGallons: 35,
		// high-roof 15-pass: ~9 ft tall, ~9,500 lb GVWR
		clearance: { heightM: 2.8, widthM: 2.1, weightTonnes: 4.3 },
		auxPower: { label: 'Generator', gph: 0.5 },
	},
	{
		id: 'sprinter',
		name: 'Sprinter Van',
		description: 'Small crew, backline',
		baseMpg: 18,
		defaultFuel: 'diesel',
		weightClass: 'heavy',
		icon: 'Van',
		tankCapacityGallons: 24,
		// high-roof 3500: ~9.5 ft tall, ~11,000 lb GVWR
		clearance: { heightM: 2.9, widthM: 2.0, weightTonnes: 5.0 },
		auxPower: { label: 'Generator', gph: 0.5 },
	},
	{
		id: 'box-26',
		name: "26' Box Truck",
		description: 'Gear, backline, merch',
		baseMpg: 10,
		defaultFuel: 'diesel',
		weightClass: 'heavy',
		icon: 'Truck',
		tankCapacityGallons: 50,
		// 26' box: ~12.75 ft tall, 26,000 lb GVWR
		clearance: { heightM: 3.9, widthM: 2.6, weightTonnes: 11.8 },
		auxPower: { label: 'Reefer', gph: 0.6 },
	},
	{
		id: 'nightliner',
		name: 'Prevost Tour Bus',
		description: "45' nightliner, sleeper coach",
		baseMpg: 6,
		defaultFuel: 'diesel',
		weightClass: 'heavy',
		icon: 'BusFront',
		tankCapacityGallons: 200,
		// Prevost H3-45: ~12 ft + roof A/C pods, 102 in wide, 54,500 lb GVWR
		clearance: { heightM: 3.8, widthM: 2.6, weightTonnes: 24.7 },
		auxPower: { label: 'Generator', gph: 0.75 },
	},
	{
		id: 'semi',
		name: 'Semi / Gear Truck',
		description: 'Production trailer, full rig',
		baseMpg: 5.5,
		defaultFuel: 'diesel',
		weightClass: 'heavy',
		// Custom icon — see src/components/fuel/icons/semi-truck.ts.
		// Lucide doesn't ship a proper tractor-trailer shape; this one
		// is drawn in lucide's style specifically for our Semi row.
		icon: 'SemiTruck',
		tankCapacityGallons: 200,
		// 13.5 ft trailer, 102 in wide, 80,000 lb gross
		clearance: { heightM: 4.1, widthM: 2.6, weightTonnes: 36.3 },
		auxPower: { label: 'Reefer', gph: 0.8 },
	},
]

export function getVehicle(id: string): Vehicle {
	return VEHICLES.find((v) => v.id === id) ?? (VEHICLES[0] as Vehicle)
}
